import { computed, onMounted, ref } from 'vue';
import { useFlowRuleName, useI18n } from '@/composables';
import { FlowHierarchyTypeEnum, StatusEnum } from '@/services/models/Enums';
import { cloneDeep } from 'lodash';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import { getFlowApiRequest } from '@/services/api/flows-api';
import { getCampaignApiRequest } from '@/services/api/campaign-api';
import { useStore } from '@/store';

export type LpPixelDialogData = {
  id?: string;
};

type SelectedLanding = {
  id: string | number;
  offers?: number;
  meta?: Record<number, number>;
};

const stringifyBeMobData = (beMobData: {
  cid?: string;
  lid?: string | number;
  fid?: string;
  rid?: number | string;
  pid?: number | string;
}) => {
  const params: Record<string, string | number> = {};

  if (beMobData.cid) {
    params.c = beMobData.cid;
  } else {
    return '';
  }

  if (beMobData.lid) {
    params.l = beMobData.lid;
  } else {
    return '';
  }

  if (beMobData.fid) {
    params.f = beMobData.fid;
  }

  if (typeof beMobData.rid !== 'undefined') {
    params.a = beMobData.rid;
  } else {
    return '';
  }

  if (typeof beMobData.pid !== 'undefined') {
    params.b = beMobData.pid;
  } else {
    return '';
  }

  const stringParts: string[] = [];
  Object.keys(params).forEach((key) => {
    stringParts.push(`${key}=${encodeURIComponent(params[key])}`);
  });

  return `ns=${encodeURIComponent(stringParts.join('..'))}`;
};

export const useLpPixelForm = (
  getData: () => LpPixelDialogData,
  emitClose: () => void,
) => {
  const { t, locale } = useI18n();
  const store = useStore();
  const show = ref(false);
  const flow = ref<Record<string, unknown>>({});
  const protocol = ref('https');
  const trackingDomainId = ref('');
  const campaignId = ref('');
  const campaign = ref<Record<string, unknown>>({});
  const selectedLanding = ref<SelectedLanding | null>(null);

  const selectedDomain = computed(
    () => store.trackingDomainsObject[trackingDomainId.value] || { domain: '' },
  );

  const trackingDomains = computed(() =>
    store.trackingDomains.data.filter((item) =>
      protocol.value === 'https' ? item.httpsAvailable : true,
    ),
  );

  const landingOffers = computed(() => selectedLanding.value?.offers || 1);

  const landingMetaDefault = {
    ruleId: 'REPLACE',
    pathId: 'REPLACE',
    landingId: 'REPLACE',
  };

  const landingMeta = computed(() => {
    if (selectedLanding.value?.meta) {
      return {
        ruleId: +selectedLanding.value.meta[FlowHierarchyTypeEnum.rule] + 1,
        pathId: +selectedLanding.value.meta[FlowHierarchyTypeEnum.path] + 1,
        landingId: +selectedLanding.value.meta[FlowHierarchyTypeEnum.landing] + 1,
      };
    }

    return landingMetaDefault;
  });

  const callbackCode = computed(() => `
function bemobCb(params) {
    var t = params.trackingDomain+'/click/';
    var re = new RegExp(t+'?(\\\\d*)');
    var e = document.querySelectorAll('a[href*="'+t+'"]');
    for (var i = 0; i < e.length; i++) {
        var ex = re.exec(e[i].href);
        if (ex) {
            e[i].href = params.${protocol.value === 'http' ? 'ctaUrl' : 'ctaSecureUrl'}.replace('%%OFFER_NUMBER%%', ex[1] || 1);
        }
    }
}
`);

  const lpPixelParams = computed(() => {
    const { ruleId, pathId, landingId } = landingMeta.value;
    return `rule=${ruleId}&path=${pathId}&landing=${landingId}`;
  });

  const lpPixel = computed(() => {
    const lpPixelHref = `${protocol.value}://${selectedDomain.value.domain}/landing/${campaignId.value}?callback=bemobCb&${lpPixelParams.value}`;
    const lpPixelImgHref = `${protocol.value}://${selectedDomain.value.domain}/landing/${campaignId.value}?${lpPixelParams.value}`;

    let pixelScript = `!function(){var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="${lpPixelHref}&"+window.location.search.substring(1);var b=document.getElementsByTagName("script")[0];b.parentNode.insertBefore(a,b)}();`;
    pixelScript = `<scr` + `ipt type="text/javascr` + `ipt">${pixelScript}</scr` + `ipt><noscr` + `ipt><img src="${lpPixelImgHref}" alt=""></noscr` + `ipt>`;
    pixelScript = `<scr` + `ipt type="text/javascr` + `ipt">${callbackCode.value}</scr` + `ipt>\n${pixelScript}`;

    return pixelScript;
  });

  const metaTagContent = computed(
    () =>
      `<meta http-equiv="delegate-ch" content="sec-ch-ua https://${selectedDomain.value.domain}; sec-ch-ua-mobile https://${selectedDomain.value.domain}; sec-ch-ua-arch https://${selectedDomain.value.domain}; sec-ch-ua-model https://${selectedDomain.value.domain}; sec-ch-ua-platform https://${selectedDomain.value.domain}; sec-ch-ua-platform-version https://${selectedDomain.value.domain}; sec-ch-ua-bitness https://${selectedDomain.value.domain}; sec-ch-ua-full-version-list https://${selectedDomain.value.domain}; sec-ch-ua-full-version https://${selectedDomain.value.domain}">`,
  );

  const getCtaHref = (offerNumber: number) => {
    const { ruleId, pathId } = landingMeta.value;

    const noScriptBeMobData = stringifyBeMobData({
      cid: campaignId.value,
      lid: selectedLanding.value?.id,
      fid: campaign.value.destinationType !== 'flowInline' ? (campaign.value.flowId as string) : '',
      rid: ruleId !== 'REPLACE' ? +ruleId - 1 : '',
      pid: pathId !== 'REPLACE' ? +pathId - 1 : '',
    });

    return `<a href="${protocol.value}://${selectedDomain.value.domain}/click/${offerNumber}?${noScriptBeMobData}">Click me!</a>`;
  };

  const load = async () => {
    const campaignData = await getCampaignApiRequest(campaignId.value);
    campaign.value = campaignData;

    if (campaignData.destinationType === 'flowInline') {
      flow.value = campaignData.flowInline as Record<string, unknown>;
    } else {
      flow.value = await getFlowApiRequest(campaignData.flowId as string);
    }

    trackingDomainId.value = campaignData.trackingDomainId
      ? campaignData.trackingDomainId
      : store.user.trackingDomainId;
  };

  const close = () => {
    emitClose();
  };

  onMounted(async () => {
    campaignId.value = getData().id || '';
    selectedLanding.value = null;

    store.setLoading(true);

    if (!store.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }

    try {
      await load();
    } finally {
      store.setLoading(false);
      show.value = true;
    }
  });

  return {
    t,
    locale,
    store,
    show,
    flow,
    protocol,
    trackingDomainId,
    campaign,
    selectedLanding,
    trackingDomains,
    landingOffers,
    lpPixel,
    metaTagContent,
    getCtaHref,
    close,
  };
};
